import { useEffect, useState, useCallback } from 'preact/hooks';

import { observeViewportResize } from '../../../utils/observeViewportResize';

import { usePlayerContainer } from './usePlayerContainer';
import { useJWPlayerEvent } from './useJWPlayerEvent';

/**
 * @see {https://github.com/jwplayer/jwplayer/blob/master/src/js/view/utils/breakpoint.ts#L3}
 */
export const getBreakpointByWidth = (width: number): jwplayer.Breakpoint => {
  let breakpoint: jwplayer.Breakpoint = -1;

  if (width >= 1280) {
    breakpoint = 7;
  } else if (width >= 960) {
    breakpoint = 6;
  } else if (width >= 800) {
    breakpoint = 5;
  } else if (width >= 640) {
    breakpoint = 4;
  } else if (width >= 540) {
    breakpoint = 3;
  } else if (width >= 420) {
    breakpoint = 2;
  } else if (width >= 320) {
    breakpoint = 1;
  } else if (width >= 250) {
    breakpoint = 0;
  }

  return breakpoint;
};

// only missing in older safari versions https://caniuse.com/resizeobserver
const isResizeObserverSupported = 'ResizeObserver' in window;

export const useJWBreakpoint = () => {
  const playerContainer = usePlayerContainer();

  const [breakpoint, setBreakpoint] = useState(() =>
    getBreakpointByWidth(playerContainer.clientWidth)
  );

  const handleBreakpoint = useCallback((param: jwplayer.BreakpointParam) => {
    setBreakpoint(param.breakpoint);
  }, []);

  useJWPlayerEvent('breakpoint', handleBreakpoint);

  // in some cases the breakpoint class in jwplayer doesn't update properly e.g.
  // entering fullscreen mode doesn't always modify the breakpoint class,
  // so that's why we have to listen to the changes ourselves
  useEffect(() => {
    if (!isResizeObserverSupported) {
      // use window resize event as fallback if ResizeObserver isn't supported
      const { unsubscribe } = observeViewportResize(() => {
        setBreakpoint(getBreakpointByWidth(playerContainer.clientWidth));
      });

      return () => unsubscribe();
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      setBreakpoint(getBreakpointByWidth(entry.target.clientWidth));
    });

    observer.observe(playerContainer);

    return () => observer.disconnect();
  }, [playerContainer]);

  return { breakpoint };
};
